import { DndContext, DragEndEvent, rectIntersection } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IconDefinition } from "@fortawesome/pro-regular-svg-icons";
import { useInterval } from "@react-hooks-library/core";
import {
  InfiniteData,
  QueryKey,
  useInfiniteQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  type ColumnDef,
  ColumnMeta,
  Row,
  RowData,
  TableMeta,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { AssertionError } from "assert";
import { AxiosError } from "axios";
import { produce } from "immer";
import { isEqual, maxBy, set } from "lodash";
import {
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtual } from "react-virtual";
import { twJoin, twMerge } from "tailwind-merge";

import { ExporterDatasetsEndpoint } from "src/api/endpoints";
import { EnumOptionsBE, FlowVersion } from "src/api/flowTypes";
import {
  Dataset,
  DatasetColumn,
  DatasetColumnGroup,
  DatasetColumnGroupValues,
  DatasetPage,
  DatasetRow,
  DatasetRowDataGroup,
  DatasetRowDataGroupToColumnGroupMap,
  DesiredType,
} from "src/api/types";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { EventConfigEnriched } from "src/clients/features-control/api";
import { AddAdditionalFieldModal } from "src/datasets/DatasetTable/AddAdditionalFieldModal";
import { EmptyState } from "src/datasets/DatasetTable/EmptyState";
import {
  RowSkeleton,
  RowsSkeleton,
} from "src/datasets/DatasetTable/RowsSkeleton";
import { SampleIntegrationResponseCell } from "src/datasets/DatasetTable/SampleIntegrationResponseCell";
import { ReadonlyCell } from "src/datasets/DatasetTable/cells";
import {
  INDEX_COLUMN_GROUP_ID,
  INDEX_COLUMN_ID,
  useColumnAddHandler,
  useDatasetColumns,
} from "src/datasets/DatasetTable/hooks";
import {
  useDatasetEditTableActions,
  useHoveredColumnActions,
  useIsRowSelected,
} from "src/datasets/DatasetTable/stores";
import {
  DatasetContext,
  JSONValue,
  VersionSchemas,
} from "src/datasets/DatasetTable/types";
import { useCustomScrollReceiver } from "src/datasets/DatasetTable/useCustomScroll";
import { useKeyboardNavigation } from "src/datasets/DatasetTable/useKeyboardNavigation";
import {
  CellId,
  SUB_COLUMN_SEPARATOR,
  isRowEmpty,
} from "src/datasets/DatasetTable/utils";
import {
  DeleteColumnPayload,
  RenameColumnPayload,
  useDeleteColumn,
  useFillColumn,
  usePatchDataset,
  usePatchRow,
  usePutColumns,
  useRenameColumn,
} from "src/datasets/api/queries";
import { EditableCell } from "src/datasets/components/EditableCell";
import {
  DatasetIntegrationNode,
  DatasetIssue,
  DatasetIssues,
  isDatasetEmpty,
} from "src/datasets/utils";
import { TAKTILE_TEAM_NOTIFIED } from "src/design-system/Toast/constants";
import { toastActions } from "src/design-system/Toast/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { useOutcomeTypes } from "src/outcomes/queries";
import { useFlowContext } from "src/router/routerContextHooks";
import { CaseSchemaWithMetadata } from "src/schema/schemaEditor/CaseSchemaEditor";
import { logger } from "src/utils/logger";

export const DATASET_INITIAL_PAGE_SIZE = 150;
export const MIN_DATASET_PAGE_SIZE = 10;
const POLL_INTERVAL_MS = 8000;

const RETRYABLE_STATUS_CODES = [413, 408] as const;

/**
 * Fetches dataset rows with automatic retry on 413 (Payload Too Large) and 408 (Request Timeout) errors.
 * On each retry, the page size is halved until it reaches the minimum.
 * This handles cases where large mock data causes the response to exceed size limits or timeout.
 */
export const fetchDatasetWithRetry = async (
  baseUrl: string,
  datasetId: string,
  from: number,
  initialPageSize: number,
  minPageSize: number = MIN_DATASET_PAGE_SIZE,
): ReturnType<typeof ExporterDatasetsEndpoint.getDataset> => {
  let currentPageSize = initialPageSize;

  while (currentPageSize >= minPageSize) {
    try {
      const data = await ExporterDatasetsEndpoint.getDataset(
        baseUrl,
        datasetId,
        { from, size: currentPageSize },
      );
      return data;
    } catch (error) {
      const statusCode =
        error instanceof AxiosError ? error.response?.status : undefined;
      const isRetryableError =
        statusCode !== undefined &&
        RETRYABLE_STATUS_CODES.includes(
          statusCode as (typeof RETRYABLE_STATUS_CODES)[number],
        );

      if (!isRetryableError) {
        throw error;
      }

      const halvedPageSize = Math.floor(currentPageSize / 2);
      // Use minPageSize as final fallback if halving would go below it
      const nextPageSize =
        halvedPageSize < minPageSize ? minPageSize : halvedPageSize;

      // If we're already at minPageSize and still failing, give up
      if (currentPageSize <= minPageSize) {
        logger.warn(
          `Dataset fetch failed with ${statusCode} at minimum page size ${currentPageSize}`,
        );
        throw error;
      }

      logger.warn(
        `Dataset fetch returned ${statusCode}, retrying with reduced page size: ${currentPageSize} -> ${nextPageSize}`,
      );
      currentPageSize = nextPageSize;
    }
  }

  // This should not be reached, but TypeScript needs it
  throw new Error("Failed to fetch dataset after all retries");
};

export type RowUpdateFunction = (
  row: Row<DatasetRow>,
  columnId: string,
  value: JSONValue | undefined,
) => void;

export type DatasetEditTableRef = {
  scrollToBottom: () => void;
  openAddAdditionalColumnModal: () => void;
};

declare module "@tanstack/react-table" {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface TableMeta<TData extends RowData> {
    readonly?: boolean;
    onRowUpdate?: RowUpdateFunction;
    scrollToBottom?: () => void;
  }
}

declare module "@tanstack/table-core" {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    customId?: string;
    isColumnReorderable?: boolean;
    resource?:
      | EntitySchemaResource
      | EventConfigEnriched
      | CaseSchemaWithMetadata;
    archetype?: {
      name: string;
      desiredType: DesiredType;
      nullable: boolean;
      required: boolean;
      options?: EnumOptionsBE | null;
      matchingIntegrationNode?: DatasetIntegrationNode;
      matchingParentIntegrationNode?: DatasetIntegrationNode;
      columnIssue?: DatasetIssue;
      disabled?: boolean;
      relationSchema?: string;
      customIcon?: IconDefinition;
    };
  }
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function assertArchetypeMetadata(
  archetypeMeta: ColumnMeta<RowData, unknown>["archetype"],
): asserts archetypeMeta {
  if (!archetypeMeta) {
    throw new AssertionError({ message: "Archetype metadata must exist" });
  }
}

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function assertOnRowUpdate(
  onRowUpdate: TableMeta<RowData>["onRowUpdate"],
): asserts onRowUpdate {
  if (!onRowUpdate) {
    throw new AssertionError({
      message: "onRowUpdate must exist for the editable table",
    });
  }
}

type DatasetEditTableProps = {
  dataset: Dataset;
  schemas: VersionSchemas;
  readonly?: boolean;
  issues?: DatasetIssues;
  version?: FlowVersion;
  context?: DatasetContext;
};

const defaultColumn: Partial<ColumnDef<DatasetRow>> = {
  cell: ({ getValue, table, row, column, cell }) => {
    const cellValue = getValue() as JSONValue | undefined;

    if (table.options.meta?.readonly) {
      return (
        <ReadonlyCell
          withPopup={["any", "object", "array"].includes(
            column.columnDef.meta?.archetype?.desiredType ?? "",
          )}
        >
          {JSON.stringify(cellValue)}
        </ReadonlyCell>
      );
    }

    assertArchetypeMetadata(column.columnDef.meta?.archetype);
    assertOnRowUpdate(table.options.meta?.onRowUpdate);

    const { onRowUpdate } = table.options.meta;
    const { archetype: archetypeMeta } = column.columnDef.meta;

    const showSampleIntegrationResponseCell =
      archetypeMeta.matchingIntegrationNode && !cellValue;

    if (
      showSampleIntegrationResponseCell &&
      archetypeMeta.matchingIntegrationNode
    ) {
      return (
        <SampleIntegrationResponseCell
          cellId={cell.id as CellId}
          column={column}
          disabled={!!archetypeMeta.disabled}
          integrationNode={archetypeMeta.matchingIntegrationNode}
          row={row}
          onChange={onRowUpdate}
        />
      );
    } else {
      return (
        <EditableCell
          cellId={cell.id as CellId}
          column={column}
          columnIssue={archetypeMeta.columnIssue}
          desiredType={archetypeMeta.desiredType}
          disabled={!!archetypeMeta.disabled}
          integrationNode={archetypeMeta.matchingIntegrationNode}
          nullable={archetypeMeta.nullable}
          options={archetypeMeta.options}
          relationSchema={archetypeMeta.relationSchema}
          required={archetypeMeta.required}
          row={row}
          value={cellValue}
          onChange={onRowUpdate}
        />
      );
    }
  },
  minSize: 200,
  maxSize: 400,
};

const buildColumnOrderKey = (columns: DatasetColumn[]) =>
  columns.map((col) => col.name).join(",");

const extractRowGroups = (
  over: NonNullable<DragEndEvent["over"]>,
  active: DragEndEvent["active"],
) => {
  const overGroup = over.id.toString().split(".")[0] as DatasetRowDataGroup;
  const activeGroup = active.id.toString().split(".")[0] as DatasetRowDataGroup;

  const overColumn = over.id.toString().replace(`${overGroup}.`, "");
  const activeColumn = active.id.toString().replace(`${activeGroup}.`, "");

  const overSubGroup =
    overColumn.split(SUB_COLUMN_SEPARATOR).length > 1
      ? overColumn.split(SUB_COLUMN_SEPARATOR)[0]
      : null;
  const activeSubGroup =
    activeColumn.split(SUB_COLUMN_SEPARATOR).length > 1
      ? activeColumn.split(SUB_COLUMN_SEPARATOR)[0]
      : null;

  return { overGroup, activeGroup, overSubGroup, activeSubGroup };
};

// We need this because issues is passed to useDatasetColumns
// and it expects it to be stable
const ISSUES_DEFAULT_VALUE = {};

export const DatasetEditTable = forwardRef<
  DatasetEditTableRef,
  DatasetEditTableProps
>(
  (
    {
      dataset,
      schemas,
      readonly = false,
      issues = ISSUES_DEFAULT_VALUE,
      version,
      context = "authoring",
    },
    componentRef,
  ) => {
    const { workspace, flow } = useFlowContext();
    const { setColumnId: setHoveredColumnId } = useHoveredColumnActions();
    const { editCell, selectCell, reset } = useDatasetEditTableActions();

    const [showAddAdditionalFieldModal, setShowAddAdditionalFieldModal] =
      useState(false);

    const patchRow = usePatchRow(dataset.id, flow.id, workspace?.base_url);
    const [columnDeleteConfirmPayload, setColumnDeleteConfirmPayload] =
      useState<DeleteColumnPayload | null>(null);
    const { columnAddHandler, subMocksColumnAddHandler } = useColumnAddHandler({
      dataset,
      flowId: flow.id,
      baseUrl: workspace?.base_url,
      schemas,
    });
    const deleteColumn = useDeleteColumn(
      dataset.id,
      flow.id,
      workspace?.base_url,
    );
    const renameColumn = useRenameColumn(
      dataset.id,
      flow.id,
      workspace?.base_url,
    );
    const putColumn = usePutColumns(dataset.id, flow.id, workspace?.base_url);
    const fillColumn = useFillColumn(dataset.id, flow.id, workspace?.base_url);
    const patchDataset = usePatchDataset(flow.id, workspace?.base_url);
    const queryClient = useQueryClient();
    const rowUpdateHandler: RowUpdateFunction = async (
      row,
      columnId,
      value,
    ) => {
      try {
        const newRowValue = produce(row.original, (state) => {
          const columnIdParts = columnId.split(".");
          const columnGroup = columnIdParts[0] as DatasetRowDataGroup;

          // in case of mock data, we allow `.` in the column/node names
          // so we need to set the value at the correct path
          // e.g. mock_data["Response 4.0"] = "test" should not be set as { "mock_data": { "Response 4": { "0": "test" } } }
          if (columnGroup === "mock_data") {
            const columnIdPath = columnIdParts.slice(1).join(".");
            if (!state[columnGroup]) {
              state[columnGroup] = {};
            }

            state[columnGroup][columnIdPath] = value;
          } else {
            set(state, columnId, value);
          }
        });

        // immer doesn't change original reference if no actual changes were made
        if (newRowValue !== row.original) {
          await patchRow.mutateAsync(newRowValue);
        }
      } catch (e) {
        toastActions.failure({
          title: "Failed to update row",
          description: TAKTILE_TEAM_NOTIFIED,
        });
        logger.error(e);
      }
    };
    const columnDeleteConfirmHandler = useCallback(
      async (payloads: DeleteColumnPayload) => {
        setColumnDeleteConfirmPayload(null);
        try {
          await deleteColumn.mutateAsync(payloads);
        } catch (e) {
          toastActions.failure({
            title: `Failed to delete the column${
              payloads.length > 1 ? "s" : ""
            } "${payloads.map((p) => p.name).join(", ")}"`,
            description: TAKTILE_TEAM_NOTIFIED,
          });
          logger.error(e);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [deleteColumn.mutateAsync],
    );

    const subMocksColumnDeleteHandler = useCallback(
      async (subMockColumnName: string | null, mockColumnName: string) => {
        try {
          const mockColumn = dataset.mock_columns.find(
            (col) => col.name === mockColumnName,
          );
          if (!mockColumn || !mockColumn.use_subflow_mocks) return;

          const childColumns = dataset.mock_columns
            .filter((col) =>
              col.name.startsWith(`${mockColumnName}${SUB_COLUMN_SEPARATOR}`),
            )
            .map(({ name }) => ({
              name,
              group: DatasetColumnGroupValues.mock_columns,
            }));

          const columnsToDelete =
            subMockColumnName === null
              ? childColumns
              : ([
                  {
                    name: subMockColumnName,
                    group: DatasetColumnGroupValues.mock_columns,
                  },
                ] as DeleteColumnPayload);

          await putColumn.mutateAsync([
            {
              column: {
                desired_type: mockColumn.desired_type,
                use_subflow_mocks: childColumns.length > columnsToDelete.length,
                name: mockColumnName,
              },
              group: DatasetColumnGroupValues.mock_columns,
            },
          ]);

          await deleteColumn.mutateAsync(columnsToDelete);
        } catch (e) {
          toastActions.failure({
            title: `Failed to delete "${
              subMockColumnName ? subMockColumnName : "all"
            } submock"`,
            description: TAKTILE_TEAM_NOTIFIED,
          });
          logger.error(e);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [putColumn.mutateAsync, dataset.mock_columns],
    );

    const columnsDeleteHandler = useCallback(
      (payload: DeleteColumnPayload) => setColumnDeleteConfirmPayload(payload),
      [],
    );

    const columnRenameHandler = useCallback(
      async (payload: RenameColumnPayload) => {
        const newNameIsEmpty = !payload.newName;
        if (newNameIsEmpty) return;

        if (payload.newName === payload.oldName) return;

        const newNameAlreadyExists = dataset[payload.group].find(
          (c) => c.name === payload.newName,
        );

        if (newNameAlreadyExists) {
          toastActions.failure({
            title: `${payload.newName} already exists`,
            description: "Please use a different column name",
          });
          return;
        }

        try {
          await renameColumn.mutateAsync(payload);
        } catch (e) {
          toastActions.failure({
            title: `Failed to rename the column "${payload.oldName}"`,
            description: TAKTILE_TEAM_NOTIFIED,
          });
          logger.error(e);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dataset, renameColumn.mutateAsync],
    );

    const columnTypeChangeHandler = useCallback(
      async (
        name: string,
        group: DatasetColumnGroup,
        desiredType: DesiredType,
      ) => {
        const putColumnPayload = [
          {
            column: {
              name,
              desired_type: desiredType,
              use_subflow_mocks: false,
            },
            group,
          },
        ];
        try {
          await putColumn.mutateAsync(putColumnPayload);
        } catch (e) {
          toastActions.failure({
            title: `Failed to change the type of the column "${name}"`,
            description: TAKTILE_TEAM_NOTIFIED,
          });
          logger.error(e);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [putColumn.mutateAsync],
    );

    const fillColumnHandler = useCallback(
      async (name: string, group: DatasetColumnGroup, fillValue: JSONValue) => {
        try {
          await fillColumn.mutateAsync({ name, group, fillValue });
        } catch (e) {
          toastActions.failure({
            title: `Failed to fill the column "${name}"`,
            description: TAKTILE_TEAM_NOTIFIED,
          });
          logger.error(e);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [fillColumn.mutateAsync],
    );

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const {
      data: datasetPages,
      isFetching,
      isLoading,
      hasNextPage: _queryHasNextPage,
      fetchNextPage,
    } = useInfiniteQuery<
      DatasetPage,
      Error,
      InfiniteData<DatasetPage>,
      QueryKey,
      {
        from: number;
        previousPageSize: number | undefined;
      }
    >({
      queryKey: ["datasetRows", dataset.id],
      queryFn: async ({ pageParam }) => {
        const data = await fetchDatasetWithRetry(
          workspace.base_url!,
          dataset.id,
          pageParam.from,
          pageParam.previousPageSize || DATASET_INITIAL_PAGE_SIZE,
        );

        const { dataset: freshDataset, ...rest } = data;

        const datasetsQueryKey = ["datasets", workspace.base_url, flow.id];

        const oldDatasets =
          queryClient.getQueryData<Dataset[]>(datasetsQueryKey)!;

        const datasetIndex = oldDatasets.findIndex((d) => d.id === dataset.id);
        const mustUpdateDatasets =
          datasetIndex !== -1 &&
          oldDatasets[datasetIndex].etag !== freshDataset.etag;

        if (mustUpdateDatasets) {
          const updatedDatasets = produce(oldDatasets, (draft) => {
            const dataset = draft[datasetIndex];
            Object.keys(dataset).forEach((key) => {
              const datasetKey = key as keyof Dataset;
              const oldValue = dataset[datasetKey];
              const newValue = freshDataset[datasetKey];

              // Update only if the value has changed
              if (!isEqual(oldValue, newValue)) {
                (dataset[datasetKey] as unknown) = newValue;
              }
            });
            draft.sort(
              (a, b) =>
                new Date(b.updated_at).valueOf() -
                new Date(a.updated_at).valueOf(),
            );
          });

          queryClient.setQueryData<Dataset[]>(
            datasetsQueryKey,
            updatedDatasets,
          )!;
        }

        return {
          ...rest,
          fetchedAt: Date.now(),
        };
      },
      initialPageParam: { from: 0, previousPageSize: undefined },
      getNextPageParam: (lastPage, allPages) => {
        const lastUpdatedPage = maxBy(allPages, "fetchedAt");
        if (!lastUpdatedPage) return undefined;

        if (lastPage.from_ + lastPage.size < lastUpdatedPage.total) {
          return {
            from: lastPage.from_ + lastPage.size,
            previousPageSize: lastPage.size,
          };
        }
      },
    });

    // For simulation datasets we only want to display the first page
    const hasNextPage = dataset.purpose !== "simulation" && _queryHasNextPage;

    const datasetRows = useMemo(() => {
      return datasetPages?.pages
        ? datasetPages.pages.flatMap((page) => page.rows)
        : [];
    }, [datasetPages?.pages]);

    const totalDisplayableRowsInDataset = useMemo(() => {
      const latestPage = maxBy(datasetPages?.pages, "fetchedAt");
      const actualTotal = latestPage?.total ?? 0;
      if (dataset.purpose === "simulation") {
        // Use the actual fetched size, which may be reduced due to 413/408 retry
        const fetchedSize = latestPage?.size ?? DATASET_INITIAL_PAGE_SIZE;
        return Math.min(fetchedSize, actualTotal);
      }
      return actualTotal;
    }, [datasetPages?.pages, dataset.purpose]);

    const addAdditionalColumnHandler = useCallback(
      () => setShowAddAdditionalFieldModal(true),
      [],
    );

    const { data: outcomeTypes } = useOutcomeTypes({
      flowId: flow.id,
    });

    const { columns } = useDatasetColumns({
      context: context,
      dataset,
      schemas,
      version,
      outcomeTypes,
      onColumnsDelete: columnsDeleteHandler,
      onColumnRename: useCallback(
        (group, oldName, newName, desiredType, hasSubflowMocks) =>
          columnRenameHandler({
            group,
            oldName,
            newName,
            desiredType,
            hasSubflowMocks,
          }),
        [columnRenameHandler],
      ),
      onColumnAdd: columnAddHandler,
      onAddSubMocks: subMocksColumnAddHandler,
      onDeleteSubMocks: subMocksColumnDeleteHandler,
      onColumnTypeChange: columnTypeChangeHandler,
      issues,
      onColumnFill: fillColumnHandler,
      onAddAdditionalColumn: addAdditionalColumnHandler,
    });

    const scrollToBottom = () => {
      tableContainerRef.current?.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "instant",
      });
    };

    const table = useReactTable({
      data: datasetRows,
      columns,
      defaultColumn,
      getCoreRowModel: getCoreRowModel(),
      columnResizeMode: "onChange",
      meta: {
        readonly,
        onRowUpdate: rowUpdateHandler,
        scrollToBottom,
      },
    });

    const ref = useRef<HTMLTableElement>(null);

    const { rows } = table.getRowModel();
    const rowVirtualizer = useVirtual({
      parentRef: tableContainerRef,
      size: totalDisplayableRowsInDataset,
      overscan: 10,
    });
    const { virtualItems: virtualRows, totalSize } = rowVirtualizer;

    useImperativeHandle(componentRef, () => ({
      scrollToBottom,
      openAddAdditionalColumnModal: addAdditionalColumnHandler,
    }));

    const paddingTop =
      virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;
    const paddingBottom =
      virtualRows.length > 0
        ? totalSize - (virtualRows?.[virtualRows.length - 1]?.end || 0)
        : 0;

    useEffect(() => {
      const notFetchedRows = virtualRows.filter((row) => !rows[row.index]);
      if (notFetchedRows.length && !isFetching && hasNextPage) {
        fetchNextPage();
      }
    }, [fetchNextPage, isFetching, rows, virtualRows, hasNextPage]);

    useInterval(() => {
      if (document.visibilityState === "visible") {
        // Change code to only refetch visible rows here (refetch page is deprecated and should be removed)
        queryClient.invalidateQueries({
          queryKey: ["datasetRows", dataset.id],
        });
      }
    }, POLL_INTERVAL_MS);

    const leafColumns = table.getAllLeafColumns();
    const reorderableColumnIds = table
      .getAllFlatColumns()
      .filter(
        (c) => c.id.includes(".") && c.columnDef.meta?.isColumnReorderable,
      )
      .map((c) => c.id);

    const onDragEnd = async ({ over, active }: DragEndEvent) => {
      if (!over) return;

      const activeIndex = reorderableColumnIds.findIndex(
        (id) => id === active.id,
      );
      const overIndex = reorderableColumnIds.findIndex((id) => id === over.id);
      const { activeGroup, activeSubGroup, overGroup, overSubGroup } =
        extractRowGroups(over, active);

      const isValidColumnMove =
        activeGroup === overGroup && activeSubGroup === overSubGroup;

      if (!isValidColumnMove) {
        return;
      }

      const newColumnIdsArray = arrayMove(
        reorderableColumnIds,
        activeIndex,
        overIndex,
      );

      const newColumnIdsArrayFilteredByGroup = newColumnIdsArray
        .filter((id) => id.startsWith(activeGroup))
        .map((id) => id.replace(`${activeGroup}.`, ""));

      const columnGroup = DatasetRowDataGroupToColumnGroupMap[activeGroup];

      const currentColumnsOfGroup = dataset[columnGroup];

      const updatedColumnsOfGroup: DatasetColumn[] =
        newColumnIdsArrayFilteredByGroup.map((name) => {
          const column: DatasetColumn = currentColumnsOfGroup.find(
            (c) => c.name === name,
          )!;

          return column;
        });

      const orderHasChanged =
        buildColumnOrderKey(currentColumnsOfGroup) !==
        buildColumnOrderKey(updatedColumnsOfGroup);

      if (orderHasChanged) {
        try {
          await patchDataset.mutateAsync({
            id: dataset.id,
            etag: dataset.etag,
            patch: { [columnGroup]: updatedColumnsOfGroup },
          });
        } catch (err) {
          if (err instanceof AxiosError && err.response?.status === 412) {
            logger.warn(
              `Race condition detected when trying to reorder columns`,
            );
          } else {
            toastActions.failure({
              title: `Failed to reorder columns`,
              description: TAKTILE_TEAM_NOTIFIED,
            });
            logger.error(err);
          }
        }
      }
    };

    useCustomScrollReceiver(tableContainerRef);

    const maxHeaderDepth =
      Math.max(
        ...table
          .getHeaderGroups()
          .flatMap((group) => group.headers.map((header) => header.depth)),
        0,
      ) + 1;

    return (
      <>
        <div
          ref={tableContainerRef}
          className={twJoin(
            "h-full",
            !isDatasetEmpty(dataset) && "decideScrollbar overflow-auto",
          )}
        >
          {!isDatasetEmpty(dataset) ? (
            <table
              ref={ref}
              className="min-w-full border-collapse border-spacing-0"
              data-loc="dataset-editor-table"
              style={{
                width: table.getCenterTotalSize(),
              }}
              onMouseLeave={() => setHoveredColumnId(null)}
            >
              <thead className="sticky top-0 z-10 bg-white">
                <DndContext
                  accessibility={{
                    container: tableContainerRef.current
                      ? tableContainerRef.current
                      : undefined,
                  }}
                  collisionDetection={rectIntersection}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    disabled={readonly}
                    items={reorderableColumnIds}
                    strategy={horizontalListSortingStrategy}
                  >
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        onClick={() => headerGroup.id === "0" && reset()}
                      >
                        {headerGroup.headers.map((header) => {
                          const isLeafHeader =
                            header.depth - maxHeaderDepth === -1;
                          const stickToTop =
                            header.id !== INDEX_COLUMN_ID &&
                            isLeafHeader &&
                            maxHeaderDepth > 3 &&
                            header.column.depth === 1;

                          if (header.isPlaceholder) {
                            return (
                              <th
                                key={header.id}
                                className={twJoin(
                                  "relative border border-y-0 border-gray-100 p-0",
                                  header.id.includes(INDEX_COLUMN_ID) &&
                                    "bg-gray-50",
                                  !header.subHeaders.length && "min-w-[8rem]",
                                )}
                                colSpan={header.colSpan}
                              />
                            );
                          }

                          return (
                            <th
                              key={header.id}
                              className={twJoin(
                                "relative border border-gray-100 p-0 text-left",
                                "first:border-l-0 last:border-l-0",
                                stickToTop && "-translate-y-full",
                                header.depth === 2 && maxHeaderDepth > 3
                                  ? "border-y-transparent"
                                  : "border-y-0",
                                [
                                  INDEX_COLUMN_ID,
                                  INDEX_COLUMN_GROUP_ID,
                                ].includes(header.column.id) &&
                                  "sticky left-0 z-20 bg-gray-50",
                              )}
                              colSpan={header.colSpan}
                              style={{
                                width: !header.subHeaders.length
                                  ? header.getSize()
                                  : undefined,
                                maxWidth: !header.subHeaders.length
                                  ? header.column.getSize()
                                  : undefined,
                                minWidth: !header.subHeaders.length
                                  ? header.column.getSize()
                                  : undefined,
                              }}
                              onMouseDownCapture={() => {
                                if (!readonly) {
                                  if (
                                    header.column.columnDef.meta?.archetype
                                      ?.disabled
                                  ) {
                                    selectCell(
                                      `${header.depth - maxHeaderDepth}_${
                                        header.column.columnDef.meta.customId ??
                                        header.id
                                      }`,
                                    );
                                  } else {
                                    editCell(
                                      `${header.depth - maxHeaderDepth}_${
                                        header.column.columnDef.meta
                                          ?.customId ?? header.id
                                      }`,
                                    );
                                  }
                                }
                              }}
                              onMouseOver={() => {
                                if (!readonly) {
                                  setHoveredColumnId(header.id);
                                }
                              }}
                            >
                              {header.column.columnDef.meta
                                ?.isColumnReorderable === true ? (
                                <SortableWrapper
                                  disabled={readonly}
                                  id={header.column.id}
                                >
                                  {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext(),
                                  )}
                                </SortableWrapper>
                              ) : (
                                flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )
                              )}
                              {!readonly &&
                                !header.subHeaders.length &&
                                header.column.getCanResize() && (
                                  <div
                                    className={twJoin(
                                      "transform-colors absolute -bottom-px -top-px right-0 w-1.5 translate-x-1/2",
                                      "cursor-col-resize select-none bg-indigo-500 opacity-0 duration-150 hover:opacity-100",
                                      header.column.getIsResizing() &&
                                        "opacity-100",
                                    )}
                                    onMouseDown={header.getResizeHandler()}
                                    onTouchStart={header.getResizeHandler()}
                                  />
                                )}
                            </th>
                          );
                        })}
                      </tr>
                    ))}
                  </SortableContext>
                </DndContext>
              </thead>
              <tbody>
                {paddingTop > 0 && (
                  <tr>
                    <td style={{ height: `${paddingTop}px` }} />
                  </tr>
                )}
                {virtualRows.map((virtualRow) => {
                  const row = rows[virtualRow.index];
                  if (row) {
                    return (
                      <RowWrapper
                        key={row.id}
                        ref={virtualRow.measureRef}
                        index={row.index}
                        isEmpty={!readonly && isRowEmpty(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td
                            key={cell.id}
                            className={twJoin(
                              "h-8 border border-gray-100 p-0 group-[.is-empty]:border-yellow-100",
                              "first:border-l-0 last:border-l-0",
                              INDEX_COLUMN_ID === cell.column.id &&
                                "sticky left-0 bg-gray-50",
                            )}
                            data-loc={`dataset-table-cell-${cell.column.id}`}
                            style={{
                              width: cell.column.getSize(),
                              maxWidth: cell.column.getSize(),
                              minWidth: cell.column.getSize(),
                            }}
                            onClickCapture={() =>
                              !readonly &&
                              INDEX_COLUMN_ID !== cell.column.id &&
                              selectCell(cell.id as CellId)
                            }
                            onDoubleClickCapture={() =>
                              !readonly &&
                              INDEX_COLUMN_ID !== cell.column.id &&
                              !cell.column.columnDef.meta?.archetype
                                ?.disabled &&
                              editCell(cell.id as CellId)
                            }
                            onMouseEnter={() =>
                              !readonly && setHoveredColumnId(cell.column.id)
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </td>
                        ))}
                      </RowWrapper>
                    );
                  } else {
                    return (
                      <RowSkeleton
                        key={virtualRow.index}
                        ref={virtualRow.measureRef}
                        nCol={leafColumns.length}
                      />
                    );
                  }
                })}

                {isLoading && (
                  <RowsSkeleton
                    heightToFill={
                      tableContainerRef.current
                        ? tableContainerRef.current.clientHeight - 64
                        : 0
                    }
                    nCol={leafColumns.length}
                  />
                )}
                {paddingBottom > 0 && (
                  <tr>
                    <td style={{ height: `${paddingBottom}px` }} />
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <EmptyState
              context={context}
              version={version}
              onAddAdditionalColumn={addAdditionalColumnHandler}
              onAddColumn={columnAddHandler}
            />
          )}
        </div>
        <ConfirmationModal
          confirmationButtonText="Yes, delete"
          open={!!columnDeleteConfirmPayload}
          title="Delete field"
          variant="danger"
          onClose={() => setColumnDeleteConfirmPayload(null)}
          onConfirm={() =>
            columnDeleteConfirmHandler(columnDeleteConfirmPayload!)
          }
        >
          <div className="text-gray-500 font-inter-normal-12px">
            Are you sure you want to delete this field from dataset? This action
            cannot be undone.
          </div>
        </ConfirmationModal>
        <AddAdditionalFieldModal
          open={showAddAdditionalFieldModal}
          onAddColumn={columnAddHandler}
          onClose={() => {
            setShowAddAdditionalFieldModal(false);
          }}
        />
        {!readonly && (
          <KeyboardNavigationController
            columns={table.getAllLeafColumns()}
            rowsCount={datasetRows.length}
            tableRef={ref}
          />
        )}
      </>
    );
  },
);

const SortableWrapper: React.FC<{
  children: ReactNode;
  id: string;
  disabled: boolean;
}> = ({ children, id, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    // The component renders a couple of times with outdated columns
    // after calling mutate at onDragEnd. This is more noticeable
    // if animateLayoutChanges is enabled
    animateLayoutChanges: () => false,
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      className={twJoin("relative", isDragging && "z-10 shadow-xl")}
      style={style}
    >
      <div
        {...attributes}
        {...listeners}
        ref={setActivatorNodeRef}
        className={twMerge(
          "absolute left-1.5 top-2 h-3 w-3",
          isDragging ? "cursor-grabbing" : "cursor-grab",
          disabled && "cursor-default",
        )}
      />
      {children}
    </div>
  );
};

const KeyboardNavigationController: React.FC<{
  tableRef: React.RefObject<HTMLTableElement>;
  rowsCount: number;
  columns: ColumnDef<DatasetRow>[];
}> = ({ tableRef, rowsCount, columns }) => {
  useKeyboardNavigation(tableRef, {
    rowsLength: rowsCount,
    columns,
  });
  return null;
};

type RowWrapperProps = {
  children: ReactNode;
  index: number;
  isEmpty: boolean;
};

const RowWrapper = forwardRef<HTMLTableRowElement, RowWrapperProps>(
  ({ children, index, isEmpty }, ref) => {
    const isRowSelected = useIsRowSelected(index);
    return (
      <tr
        ref={ref}
        className={twJoin(
          "group",
          isEmpty && "is-empty bg-yellow-50",
          isRowSelected &&
            "border border-indigo-600 bg-indigo-50 outline outline-1 outline-indigo-600",
        )}
        data-loc="dataset-table-row"
      >
        {children}
      </tr>
    );
  },
);
